<template>
  <div class="kb-page" v-if="product">
    <AppHeader />

    <!-- Product Hero -->
    <section class="kb-product-hero">
      <div class="kb-product-hero__bg">
        <img :src="product.heroImage" :alt="product.name" />
        <div class="kb-product-hero__overlay"></div>
      </div>
      <div class="container-xl kb-product-hero__content" data-aos="fade-up">
        <div class="kb-page-hero__breadcrumb">
          <router-link to="/">Home</router-link>
          <i class="bi bi-chevron-right mx-2"></i>
          <router-link to="/products">Products</router-link>
          <i class="bi bi-chevron-right mx-2"></i>
          <span>{{ product.name }}</span>
        </div>
        <span class="kb-product-hero__cat">{{ product.category }}</span>
        <h1>{{ product.name }}</h1>
        <p>{{ product.tagline }}</p>
      </div>
    </section>

    <!-- Main Content -->
    <section class="kb-section kb-section--light">
      <div class="container-xl">
        <div class="kb-product-detail">

          <!-- Image Gallery -->
          <div class="kb-gallery" data-aos="fade-right">
            <div class="kb-gallery__main">
              <img :src="product.images[activeImg]" :alt="product.name" loading="lazy" />
              <span class="kb-gallery__badge" :style="{ background: product.badgeColor }">{{ product.badge }}</span>
            </div>
            <div class="kb-gallery__thumbs">
              <div
                v-for="(img, i) in product.images"
                :key="i"
                class="kb-gallery__thumb"
                :class="{ active: activeImg === i }"
                @click="activeImg = i"
              >
                <img :src="img" :alt="product.name + ' view ' + (i+1)" loading="lazy" />
              </div>
            </div>
          </div>

          <!-- Product Summary -->
          <div class="kb-product-summary" data-aos="fade-left" data-aos-delay="100">
            <span class="kb-section-header__tag">{{ product.category }}</span>
            <h2>{{ product.name }}</h2>
            <p class="kb-product-summary__tagline"><i class="bi bi-quote me-1"></i>{{ product.tagline }}</p>
            <p class="kb-product-summary__intro">{{ product.shortDesc }}</p>

            <!-- PDF Downloads -->
            <div class="kb-product-summary__downloads">
              <a
                v-if="product.pricePdf"
                :href="product.pricePdf"
                download
                class="kb-btn kb-btn--primary"
              >
                <i class="bi bi-file-earmark-pdf me-2"></i>{{ product.pricePdfLabel }}
              </a>
              <a
                v-if="product.secondaryPdf"
                :href="product.secondaryPdf"
                download
                class="kb-btn kb-btn--outline-dark"
              >
                <i class="bi bi-file-earmark-pdf me-2"></i>{{ product.secondaryPdfLabel }}
              </a>
              <a
                :href="`https://wa.me/919892310123?text=Hello%2C%20I%20would%20like%20to%20enquire%20about%3A%20${encodeURIComponent(product.name)}`"
                target="_blank"
                class="kb-btn kb-btn--whatsapp"
              >
                <i class="bi bi-whatsapp me-2"></i>Get a Quote
              </a>
            </div>

            <!-- Quick Feature Pills -->
            <div class="kb-product-summary__pills">
              <div v-for="f in product.features" :key="f.title" class="kb-pill">
                <i :class="f.icon" :style="{ color: f.color }"></i>
                <span>{{ f.title }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Full Description -->
    <section class="kb-section">
      <div class="container-xl">
        <div class="kb-section-header" data-aos="fade-up">
          <span class="kb-section-header__tag">Overview</span>
          <h2>Product Description</h2>
        </div>
        <div class="kb-product-intro-text" data-aos="fade-up" data-aos-delay="80">
          <p>{{ product.intro }}</p>
        </div>
      </div>
    </section>

    <!-- Features -->
    <section class="kb-section kb-section--light">
      <div class="container-xl">
        <div class="kb-section-header" data-aos="fade-up">
          <span class="kb-section-header__tag">Key Features</span>
          <h2>Why This Product Stands Out</h2>
        </div>
        <div class="kb-features-grid">
          <div
            v-for="(f, i) in product.features"
            :key="f.title"
            class="kb-feature-card"
            data-aos="fade-up"
            :data-aos-delay="i * 80"
          >
            <div class="kb-feature-card__icon" :style="{ background: f.color + '18' }">
              <i :class="f.icon" :style="{ color: f.color }"></i>
            </div>
            <h3>{{ f.title }}</h3>
            <p>{{ f.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Specs Table -->
    <section class="kb-section">
      <div class="container-xl">
        <div class="kb-specs-section">
          <div class="kb-specs-content" data-aos="fade-right">
            <span class="kb-section-header__tag">Specifications</span>
            <h2>{{ product.specsTitle }}</h2>
            <p>{{ product.specsDesc }}</p>
            <div class="kb-specs-table">
              <div class="kb-specs-table__header">
                <span v-for="h in product.specHeaders" :key="h">{{ h }}</span>
              </div>
              <div v-for="row in product.specs" :key="row.col1" class="kb-specs-table__row">
                <span class="kb-specs-table__col1">{{ row.col1 }}</span>
                <span class="kb-specs-table__col2">{{ row.col2 }}</span>
              </div>
            </div>
            <!-- Color Options for Agro Nets -->
            <div v-if="product.colorOptions" class="kb-color-options">
              <h4>Available Colors</h4>
              <div class="kb-color-grid">
                <div v-for="c in product.colorOptions" :key="c.name" class="kb-color-item">
                  <span class="kb-color-dot" :class="'kb-color-dot--' + c.name.toLowerCase()"></span>
                  <div>
                    <strong>{{ c.name }}</strong>
                    <p>{{ c.desc }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Applications -->
          <div class="kb-applications" data-aos="fade-left" data-aos-delay="100">
            <span class="kb-section-header__tag">Applications</span>
            <h2>Where It's Used</h2>
            <div class="kb-applications__grid">
              <div v-for="app in product.applications" :key="app.text" class="kb-app-item">
                <div class="kb-app-item__icon">
                  <i :class="app.icon"></i>
                </div>
                <span>{{ app.text }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Why Section -->
    <section class="kb-product-why">
      <div class="container-xl">
        <div class="kb-product-why__inner" data-aos="fade-up">
          <i class="bi bi-shield-fill-check kb-product-why__icon"></i>
          <h2>The KeBee Professional Advantage</h2>
          <p>{{ product.whySection }}</p>
          <div class="kb-product-why__actions">
            <a
              v-if="product.pricePdf"
              :href="product.pricePdf"
              download
              class="kb-btn kb-btn--primary"
            >
              <i class="bi bi-file-earmark-pdf me-2"></i>{{ product.pricePdfLabel }}
            </a>
            <a
              :href="`https://wa.me/919892310123?text=Hello%2C%20I%20would%20like%20to%20enquire%20about%3A%20${encodeURIComponent(product.name)}`"
              target="_blank"
              class="kb-btn kb-btn--outline-white"
            >
              <i class="bi bi-whatsapp me-2"></i>WhatsApp Enquiry
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Related Products -->
    <section class="kb-section kb-section--light">
      <div class="container-xl">
        <div class="kb-section-header" data-aos="fade-up">
          <span class="kb-section-header__tag">Explore More</span>
          <h2>Related Products</h2>
        </div>
        <div class="kb-related-grid">
          <div
            v-for="related in relatedProducts"
            :key="related.id"
            class="kb-related-card"
            data-aos="fade-up"
          >
            <router-link :to="`/products/${related.slug}`" class="kb-related-card__link">
              <div class="kb-related-card__img">
                <img :src="related.heroImage" :alt="related.name" loading="lazy" />
              </div>
              <div class="kb-related-card__body">
                <span>{{ related.category }}</span>
                <h4>{{ related.name }}</h4>
                <span class="kb-related-card__arrow"><i class="bi bi-arrow-right"></i></span>
              </div>
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <AppFooter />
  </div>

  <!-- 404 state -->
  <div class="kb-page" v-else>
    <AppHeader />
    <div class="kb-404">
      <i class="bi bi-exclamation-circle"></i>
      <h2>Product not found</h2>
      <router-link to="/products" class="kb-btn kb-btn--navy">Back to Products</router-link>
    </div>
    <AppFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import { setSeo } from '@/utils/seo.js'
import { products, getProductBySlug } from '@/data/products.js'

export default {
  name: 'ProductDetailPage',
  components: { AppHeader, AppFooter },
  data() {
    return {
      activeImg: 0,
      allProducts: products,
    }
  },
  computed: {
    product() {
      return getProductBySlug(this.$route.params.slug)
    },
    relatedProducts() {
      if (!this.product) return []
      return this.allProducts
        .filter(p => p.id !== this.product.id)
        .slice(0, 3)
    }
  },
  watch: {
    '$route.params.slug'() {
      this.activeImg = 0
      window.scrollTo(0, 0)
      this.applyProductSeo()
    },
    product(p) {
      if (p) this.applyProductSeo()
    }
  },
  mounted() {
    this.applyProductSeo()
  },
  methods: {
    applyProductSeo() {
      const p = this.product
      if (!p) return
      const rawImg = p.images && p.images[0] ? p.images[0] : ''
      const absImg = typeof window !== 'undefined' && rawImg
        ? window.location.origin + rawImg.replace(/^.*\/img\//, '/img/')
        : ''
      setSeo({
        title: `${p.name} – Price List, Specs & Details | KeBee Industries`,
        description: `Buy ${p.name} from KeBee Industries, Mumbai. ${p.shortDesc} Available in custom sizes. Download price list or request a bulk quote.`,
        path: `/products/${p.slug}`,
        image: absImg || undefined,
        keywords: `${p.name}, ${p.name} price, ${p.name} Mumbai, tarpaulin manufacturer, KeBee Industries`
      })
    }
  }
}
</script>

<style scoped>
/* Hero */
.kb-product-hero {
  min-height: 420px;
  position: relative;
  display: flex;
  align-items: flex-end;
  padding-bottom: 60px;
  overflow: hidden;
}
.kb-product-hero__bg {
  position: absolute;
  inset: 0;
}
.kb-product-hero__bg img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.kb-product-hero__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(15,31,61,0.75), rgba(15,31,61,0.9));
}
.kb-product-hero__content {
  position: relative;
  z-index: 2;
  padding-top: 120px;
}
.kb-page-hero__breadcrumb {
  display: flex;
  align-items: center;
  font-size: 0.83rem;
  color: rgba(255,255,255,0.6);
  margin-bottom: 1rem;
  flex-wrap: wrap;
}
.kb-page-hero__breadcrumb a {
  color: rgba(255,255,255,0.6);
  text-decoration: none;
}
.kb-page-hero__breadcrumb a:hover { color: #F59E0B; }
.kb-product-hero__cat {
  display: inline-block;
  background: rgba(245,158,11,0.2);
  border: 1px solid rgba(245,158,11,0.4);
  color: #F59E0B;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 4px 14px;
  border-radius: 50px;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  margin-bottom: 1rem;
}
.kb-product-hero__content h1 {
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.8rem, 4vw, 2.8rem);
  font-weight: 800;
  color: #fff;
  margin-bottom: 0.5rem;
}
.kb-product-hero__content p {
  color: rgba(255,255,255,0.75);
  font-size: 1.05rem;
  font-style: italic;
}

/* Detail Layout */
.kb-product-detail {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: start;
}

/* Gallery */
.kb-gallery__main {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  height: 400px;
}
.kb-gallery__main img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.kb-gallery__badge {
  position: absolute;
  top: 16px;
  right: 16px;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 5px 14px;
  border-radius: 50px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.kb-gallery__thumbs {
  display: flex;
  gap: 10px;
  margin-top: 12px;
  overflow-x: auto;
  padding-bottom: 4px;
}
.kb-gallery__thumb {
  width: 80px;
  height: 60px;
  flex-shrink: 0;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid #E5E7EB;
  transition: border-color 0.2s;
}
.kb-gallery__thumb.active { border-color: #F59E0B; }
.kb-gallery__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Summary */
.kb-product-summary h2 {
  font-family: 'Poppins', sans-serif;
  font-size: 1.8rem;
  font-weight: 800;
  color: #0F1F3D;
  margin-bottom: 0.5rem;
}
.kb-product-summary__tagline {
  font-style: italic;
  color: #F59E0B;
  font-size: 1rem;
  margin-bottom: 1rem;
}
.kb-product-summary__intro {
  color: #4B5563;
  line-height: 1.8;
  font-size: 0.95rem;
  margin-bottom: 1.5rem;
}
.kb-product-summary__downloads {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 1.75rem;
}
.kb-product-summary__pills {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.kb-pill {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #F9FAFB;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}
.kb-pill i { font-size: 1rem; }

/* Intro Text */
.kb-product-intro-text {
  background: #fff;
  border-left: 4px solid #F59E0B;
  padding: 2rem 2.5rem;
  border-radius: 0 16px 16px 0;
  font-size: 1rem;
  line-height: 1.9;
  color: #374151;
  box-shadow: 0 2px 16px rgba(15,31,61,0.06);
}

/* Features */
.kb-features-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-top: 2.5rem;
}
.kb-feature-card {
  background: #fff;
  border-radius: 16px;
  padding: 1.75rem;
  box-shadow: 0 2px 14px rgba(15,31,61,0.06);
  transition: transform 0.3s, box-shadow 0.3s;
}
.kb-feature-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 24px rgba(15,31,61,0.1);
}
.kb-feature-card__icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  margin-bottom: 1rem;
}
.kb-feature-card h3 {
  font-family: 'Poppins', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  color: #0F1F3D;
  margin-bottom: 0.5rem;
}
.kb-feature-card p {
  font-size: 0.85rem;
  color: #6B7280;
  line-height: 1.7;
  margin: 0;
}

/* Specs Section */
.kb-specs-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: start;
}
.kb-specs-content h2, .kb-applications h2 {
  font-family: 'Poppins', sans-serif;
  font-size: 1.4rem;
  font-weight: 700;
  color: #0F1F3D;
  margin-bottom: 0.5rem;
}
.kb-specs-content > p {
  color: #6B7280;
  font-size: 0.9rem;
  line-height: 1.7;
  margin-bottom: 1.5rem;
}
.kb-specs-table {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #E5E7EB;
}
.kb-specs-table__header {
  display: grid;
  grid-template-columns: 1fr 2fr;
  background: #0F1F3D;
  padding: 12px 16px;
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: rgba(255,255,255,0.8);
}
.kb-specs-table__row {
  display: grid;
  grid-template-columns: 1fr 2fr;
  padding: 14px 16px;
  border-bottom: 1px solid #F3F4F6;
  font-size: 0.88rem;
  transition: background 0.15s;
}
.kb-specs-table__row:last-child { border-bottom: none; }
.kb-specs-table__row:hover { background: #FFF7ED; }
.kb-specs-table__col1 {
  font-weight: 700;
  color: #0F1F3D;
}
.kb-specs-table__col2 { color: #4B5563; line-height: 1.5; }

/* Color Options */
.kb-color-options {
  margin-top: 2rem;
}
.kb-color-options h4 {
  font-family: 'Poppins', sans-serif;
  font-size: 0.85rem;
  font-weight: 700;
  color: #0F1F3D;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 1rem;
}
.kb-color-grid {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.kb-color-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}
.kb-color-dot {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 2px;
  border: 2px solid rgba(0,0,0,0.1);
}
.kb-color-dot--green { background: #166534; }
.kb-color-dot--red { background: #991B1B; }
.kb-color-dot--cream { background: #FEF3C7; }
.kb-color-dot--multicolor { background: linear-gradient(135deg, #3B82F6, #EC4899, #F59E0B); }
.kb-color-item strong { font-size: 0.88rem; color: #0F1F3D; }
.kb-color-item p { font-size: 0.8rem; color: #6B7280; margin: 2px 0 0; }

/* Applications */
.kb-applications__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-top: 1.5rem;
}
.kb-app-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #fff;
  border-radius: 12px;
  padding: 14px;
  box-shadow: 0 2px 10px rgba(15,31,61,0.06);
  font-size: 0.85rem;
  font-weight: 500;
  color: #374151;
}
.kb-app-item__icon {
  width: 38px;
  height: 38px;
  background: rgba(15,31,61,0.06);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  color: #0F1F3D;
  flex-shrink: 0;
}

/* Why Section */
.kb-product-why {
  background: linear-gradient(135deg, #0F1F3D 0%, #1E3A6E 100%);
  padding: 80px 0;
}
.kb-product-why__inner {
  text-align: center;
  max-width: 720px;
  margin: 0 auto;
}
.kb-product-why__icon {
  font-size: 3rem;
  color: #F59E0B;
  margin-bottom: 1.25rem;
  display: block;
}
.kb-product-why__inner h2 {
  font-family: 'Poppins', sans-serif;
  font-size: 1.75rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 1rem;
}
.kb-product-why__inner p {
  color: rgba(255,255,255,0.78);
  font-size: 0.95rem;
  line-height: 1.9;
  margin-bottom: 2rem;
}
.kb-product-why__actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

/* Related */
.kb-related-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 2.5rem;
}
.kb-related-card {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(15,31,61,0.07);
  transition: transform 0.3s;
}
.kb-related-card:hover { transform: translateY(-4px); }
.kb-related-card__link { text-decoration: none; display: block; }
.kb-related-card__img {
  height: 180px;
  overflow: hidden;
}
.kb-related-card__img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s;
}
.kb-related-card:hover .kb-related-card__img img { transform: scale(1.05); }
.kb-related-card__body {
  padding: 1.25rem;
  background: #fff;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.kb-related-card__body span {
  font-size: 0.72rem;
  color: #F59E0B;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.kb-related-card__body h4 {
  font-family: 'Poppins', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  color: #0F1F3D;
  margin: 0;
  flex: 1;
}
.kb-related-card__arrow { color: #F59E0B; font-size: 1.1rem; }

/* 404 */
.kb-404 {
  text-align: center;
  padding: 120px 0;
  color: #6B7280;
}
.kb-404 i { font-size: 4rem; margin-bottom: 1.5rem; display: block; }
.kb-404 h2 { font-family: 'Poppins', sans-serif; margin-bottom: 2rem; }

@media (max-width: 992px) {
  .kb-product-detail { grid-template-columns: 1fr; gap: 2.5rem; }
  .kb-features-grid { grid-template-columns: 1fr 1fr; }
  .kb-specs-section { grid-template-columns: 1fr; gap: 2rem; }
  .kb-related-grid { grid-template-columns: repeat(2, 1fr); }
  .kb-gallery__main { height: 320px; }
}
@media (max-width: 576px) {
  .kb-features-grid { grid-template-columns: 1fr; }
  .kb-related-grid { grid-template-columns: 1fr; }
  .kb-applications__grid { grid-template-columns: 1fr; }
  .kb-gallery__main { height: 240px; }
  .kb-product-summary__downloads { flex-direction: column; }
  .kb-product-summary__downloads .kb-btn { text-align: center; justify-content: center; }
  .kb-product-hero h1 { font-size: clamp(1.6rem, 6vw, 2.2rem); }
}
</style>
